// src/components/antenna/model-details-loading.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";

type ModelStatus = "queued" | "running" | "done" | "error";

function clampPercent(v: unknown): number {
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return 0;
  const clamped = Math.max(0, Math.min(100, n));
  return Math.round(clamped);
}

function TerminalBackground({ logs }: { logs: string[] }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [displayLogs, setDisplayLogs] = useState<string[]>([]);
  const logIndexRef = useRef(0);
  const currentLogsRef = useRef(logs);

  useEffect(() => { currentLogsRef.current = logs; }, [logs]);

  useEffect(() => {
    const initial = [...logs, ...logs, ...logs];
    setDisplayLogs(initial);
    const interval = setInterval(() => {
      setDisplayLogs((prev) => {
        const next = [...prev, currentLogsRef.current[logIndexRef.current % currentLogsRef.current.length] ?? ""];
        return next.slice(-110);
      });
      logIndexRef.current++;
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    }, 120);
    return () => clearInterval(interval);
  }, []);

  return (
    <div ref={containerRef} className="absolute inset-0 overflow-hidden pointer-events-none opacity-[0.15] blur-[2px]">
      <div className="px-16 py-8 font-mono text-[var(--text-xs)] leading-[1.6] text-[var(--foreground)] whitespace-pre min-h-full">
        {displayLogs.map((log, i) => <div key={i}>{log}</div>)}
      </div>
    </div>
  );
}

function ProgressBar({ value, height = 8, trackBg = "var(--muted)", fillBg = "var(--accent)" }: { value: any; height?: number; trackBg?: string; fillBg?: string }) {
  const pct = clampPercent(value);
  return (
    <div className="w-full rounded-full overflow-hidden border border-[var(--border)]" style={{ height, backgroundColor: trackBg }}>
      <div className="h-full rounded-full transition-all duration-300 ease-out" style={{ width: `${pct}%`, backgroundColor: fillBg }} />
    </div>
  );
}

const STAGES = [
  { id: "setup", headline: "Mapping baseline", body: "Capturing how AI currently frames your brand across selected models.", bgGradient: "radial-gradient(1200px 900px at 20% 0%, var(--spectral-violet-10) 0%, var(--background) 60%)", terminalLogs: ["Setting up monitoring pipeline...", "Configuring baseline metrics", "12 KPIs defined", "✓ Tracking active"] },
  { id: "extract", headline: "Extracting signals", body: "Pulling product attributes, category cues, and competitor comparisons.", bgGradient: "radial-gradient(1200px 900px at 80% 0%, var(--signal-blue-10) 0%, var(--background) 60%)", terminalLogs: ["Normalizing product language...", "Detecting category anchors...", "Computing consistency...", "✓ Signals extracted"] },
  { id: "analyze", headline: "Running model passes", body: "Measuring how each model recommends, ranks, and describes you.", bgGradient: "radial-gradient(1200px 900px at 30% 0%, var(--alpha-teal-10) 0%, var(--background) 60%)", terminalLogs: ["Dispatching prompts...", "Collecting responses...", "Detecting contradictions...", "✓ Model pass complete"] },
  { id: "assemble", headline: "Assembling report", body: "Generating your baseline snapshot so you can start improving outcomes.", bgGradient: "radial-gradient(1200px 900px at 70% 0%, var(--spectral-violet-10) 0%, var(--background) 60%)", terminalLogs: ["Aggregating metrics...", "Building charts...", "Drafting insights...", "✓ Report ready"] },
];

export function ModelDetailsLoading(props: {
  searchQuery: string;
  loadingProgress: number;
  currentLoadingModel?: string;
  loadingModels?: Array<{ id: string; label: string; status: ModelStatus; progress: number }>;
  onCancel: () => void;
  onReadyNavigate?: () => void;
  /**
   * True when the backend has sent the final aggregated payload (final_result).
   * Only when this flips true do we show the "Your baseline is ready" flash
   * and then trigger navigation. Reaching 100% on the progress bar is NOT
   * sufficient — Phase-2 (recommendations) and DB aggregation may still be
   * running in parallel after Phase-1 progress hits 100%.
   */
  isShowingSuccess?: boolean;
}) {
  const {
    searchQuery,
    loadingProgress,
    loadingModels = [],
    onReadyNavigate,
    isShowingSuccess,
  } = props;

  const overallPct = clampPercent(loadingProgress);

  // Until the real final-result signal arrives, never show 100% — that would
  // wrongly imply we're done. Cap at 99% with a "Finalizing…" affordance so
  // the user knows we're still working on the recommendations / save phase.
  const displayPct = isShowingSuccess ? overallPct : Math.min(99, overallPct);
  const isFinalizing = !isShowingSuccess && overallPct >= 100;

  const stageIndex = useMemo(
    () =>
      displayPct >= 85 ? 3
      : displayPct >= 60 ? 2
      : displayPct >= 30 ? 1
      : 0,
    [displayPct]
  );
  const stage = STAGES[stageIndex];

  const [showContent, setShowContent] = useState(false);
  const [showCompletion, setShowCompletion] = useState(false);

  useEffect(() => {
    setShowContent(false);
    const t = setTimeout(() => setShowContent(true), 100);
    return () => clearTimeout(t);
  }, [stageIndex]);

  // Only the explicit ready signal triggers the completion screen.
  // Progress hitting 100% on its own is NOT enough — see prop docs above.
  useEffect(() => {
    if (!isShowingSuccess) return;
    setShowCompletion(true);
    if (!onReadyNavigate) return;
    // Flash the "ready" screen briefly, then navigate.
    const t = setTimeout(() => onReadyNavigate(), 1500);
    return () => clearTimeout(t);
  }, [isShowingSuccess, onReadyNavigate]);

  if (showCompletion) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--background)] animate-in fade-in duration-700">
        <div className="text-center">
          <h1 className="text-[72px] font-bold tracking-tighter text-[var(--foreground)]">Your baseline is ready</h1>
          <p className="text-xl text-[var(--muted-foreground)] mt-4 animate-pulse">Opening snapshot...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col relative overflow-hidden transition-all duration-1000" style={{ background: stage.bgGradient }}>
      <TerminalBackground logs={stage.terminalLogs} />

      {/* Brand Name - Centered at the top */}
      <div className="absolute top-12 left-0 right-0 z-20 flex justify-center">
        <div className="text-sm font-bold text-[var(--foreground)] uppercase tracking-[0.3em] opacity-80 bg-[var(--background)]/40 px-6 py-2 rounded-full backdrop-blur-sm border border-[var(--border)]">
          {searchQuery}
        </div>
      </div>

      <div className="absolute top-12 right-12 z-20 flex gap-2">
        {STAGES.map((_, idx) => (
          <div key={idx} className="transition-all duration-300" style={{ width: idx === stageIndex ? "32px" : "8px", height: "8px", borderRadius: "4px", background: idx === stageIndex ? "var(--foreground)" : "var(--border)" }} />
        ))}
      </div>

      <div className="flex-1 flex flex-col items-center justify-center px-16 text-center z-10">
        <div className="transition-all duration-500" style={{ opacity: showContent ? 1 : 0, transform: showContent ? "translateY(0)" : "translateY(20px)" }}>
          <h1 className="text-7xl font-bold tracking-tighter mb-6">
            {isFinalizing ? "Finalizing your snapshot" : stage.headline}
          </h1>
          <p className="text-xl text-[var(--muted-foreground)] max-w-xl mx-auto">
            {isFinalizing
              ? "All models have responded. Generating recommendations and saving your baseline."
              : stage.body}
          </p>
        </div>
      </div>

      <div className="bg-[var(--card)]/80 backdrop-blur-xl border-t border-[var(--border)] p-8 z-20">
        <div className="max-w-7xl mx-auto space-y-6">
          <div className="flex justify-between text-[10px] font-black uppercase tracking-[0.2em] text-[var(--muted-foreground)]">
            <span>
              {isFinalizing ? "Finalizing recommendations" : "Synchronizing Model Perception"}
            </span>
            <span>{displayPct}%</span>
          </div>
          <ProgressBar value={displayPct} height={6} fillBg="var(--foreground)" />

          <div className="flex gap-3 overflow-x-auto pb-2 no-scrollbar">
            {loadingModels.map(m => (
              <div key={m.id}
                className="flex items-center gap-3 px-4 py-2 rounded-lg border transition-all"
                style={{
                    backgroundColor: m.status === 'done' ? 'var(--alpha-teal-5)' : 'var(--background)',
                    borderColor: m.status === 'done' ? 'var(--alpha-teal-30)' : 'var(--border)'
                }}>
                {m.status === "done" ?
                  <CheckCircle2 className="w-3.5 h-3.5 text-[var(--alpha-teal)]" /> :
                  <Loader2 className="w-3.5 h-3.5 animate-spin text-[var(--signal-blue)]" />
                }
                {/* Model Name in Var Teal */}
                <span className="text-[11px] font-bold" style={{ color: m.status === 'done' ? 'var(--alpha-teal)' : 'var(--foreground)' }}>
                    {m.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}